// Normalize any material/work item to tier 0 format
function normalizeToTier0(item) {
  // Example normalization: adjust field names, fill missing fields, sanitize values
  // You can expand this as needed to match your exact tier 0 structure
  return {
    title: item.title || item.name || "", // Ensure title is present
    base_unit: item.base_unit || item.unit || "",
    work_code: item.work_code || item.code || "",
    resource_type: item.resource_type || item.type || "",
    unit_rate: typeof item.unit_rate === "number" ? item.unit_rate : Number(item.unit_rate) || 0,
    qty_per_work_unit: typeof item.qty_per_work_unit === "number" ? item.qty_per_work_unit : Number(item.qty_per_work_unit) || 0,
    // Add more fields as needed for tier 0
  };
}
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  searchMaterials,
  fetchProductTables,
  fetchCatalogStandards,
  fetchCatalogGroups,
  fetchGroupVariants,
  fetchVariantResources,
} from "../api/productTablesApi";
import { getSafeSheetName } from "../excel/sheetUtils";
import Tier1ResourceLookup from "./Tier1ResourceLookup";

const RESOURCE_TYPES = [
  { value: "", label: "ყველა / All" },
  { value: "MATERIAL", label: "მასალა / Material" },
  { value: "LABOR", label: "ხელფასი / Labor" },
  { value: "EQUIPMENT", label: "მექანიზმი / Equipment" },
];

const FORMAT_TYPES = [
  { value: "", label: "ყველა / All" },
  { value: "tier0", label: "Tier 0" },
  { value: "tier1", label: "Tier 1" },
  { value: "standards", label: "სტანდარტები" },
];

const LANGUAGES = [
  { value: "", label: "ყველა" },
  { value: "geo", label: "ქართ." },
  { value: "eng", label: "ENG" },
  { value: "rus", label: "РУС" },
];

export default function SheetMaterialLookupPanel({ token, targetSheet, projectArea = 0 }) {
  const [query, setQuery] = useState("");
  const [resourceType, setResourceType] = useState("");
  const [tableFormat, setTableFormat] = useState("");
  const [selectedTableId, setSelectedTableId] = useState("");
  const [lang, setLang] = useState("");
  const [tables, setTables] = useState([]);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Standards browsing state
  const [catalogs, setCatalogs] = useState([]);
  const [selectedCatalogId, setSelectedCatalogId] = useState("");
  const [stdGroups, setStdGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [stdVariants, setStdVariants] = useState([]);

  // Load available tables and catalogs
  useEffect(() => {
    if (!token) return;
    fetchProductTables(token).then((data) => setTables(data || [])).catch(() => {});
    fetchCatalogStandards(token).then((data) => setCatalogs(data || [])).catch(() => {});
  }, [token]);

  // Load groups when catalog selected
  useEffect(() => {
    if (!selectedCatalogId || !token || tableFormat !== "standards") return;
    fetchCatalogGroups(token, selectedCatalogId, query)
      .then(setStdGroups).catch(() => setStdGroups([]));
  }, [selectedCatalogId, token, tableFormat]);

  // Load variants when group selected
  useEffect(() => {
    if (!selectedGroupId || !selectedCatalogId || !token) return;
    fetchGroupVariants(token, selectedCatalogId, selectedGroupId)
      .then(setStdVariants).catch(() => setStdVariants([]));
  }, [selectedGroupId, selectedCatalogId, token]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const doSearch = useCallback(
    async (q, rt, fmt, tid, ln) => {
      const searchQuery = (q || "").trim();
      setBusy(true);
      setError("");
      try {
        const data = await searchMaterials(token, searchQuery || "*", 20, rt, fmt, tid, ln);
        setResults(data || []);
        setShowDropdown(true);
      } catch (err) {
        setError("Search failed: " + (err.message || err));
        setResults([]);
      } finally {
        setBusy(false);
      }
    },
    [token]
  );

  // Load initial results on mount
  useEffect(() => {
    if (token) {
      doSearch("", resourceType, tableFormat, selectedTableId, lang);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live search on typing with debounce
  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSuccessMsg("");

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(val, resourceType, tableFormat, selectedTableId, lang);
    }, 300);
  };

  // Re-search when resource type changes
  const handleTypeChange = (rt) => {
    setResourceType(rt);
    setSuccessMsg("");
    doSearch(query, rt, tableFormat, selectedTableId, lang);
  };

  // Re-search when format changes
  const handleFormatChange = (fmt) => {
    setTableFormat(fmt);
    setSelectedTableId("");
    setSuccessMsg("");
    doSearch(query, resourceType, fmt, "", lang);
  };

  // Re-search when table changes
  const handleTableChange = (tid) => {
    setSelectedTableId(tid);
    setSuccessMsg("");
    doSearch(query, resourceType, tableFormat, tid, lang);
  };

  // Re-search when language changes
  const handleLangChange = (ln) => {
    setLang(ln);
    setSuccessMsg("");
    doSearch(query, resourceType, tableFormat, selectedTableId, ln);
  };

  // Filter tables by selected format
  const filteredTables = tableFormat
    ? tables.filter((t) => t.format === tableFormat)
    : tables;

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  /**
   * Find a safe insert row above the totals block and reserve enough space.
   * If there isn't a contiguous free block large enough, push the totals block down
   * by the exact number of missing rows so nothing gets overwritten.
   */
  const findInsertRow = async (sheet, context, rowsToInsert = 1) => {
    const usedRange = sheet.getUsedRangeOrNullObject();
    usedRange.load(["rowCount", "columnCount"]);
    await context.sync();

    if (usedRange.isNullObject || usedRange.rowCount < 9) {
      return 9;
    }

    const scanColCount = Math.max(13, usedRange.columnCount || 13);
    const scanRange = sheet.getRangeByIndexes(0, 0, usedRange.rowCount, scanColCount);
    scanRange.load("values");
    await context.sync();

    const summaryKeywords = ["ჯამი", "ზედნადები", "გეგმიური", "დაგროვება", "total", "overhead"];
    const neededRows = Math.max(1, Number(rowsToInsert) || 1);
    let summaryStartIdx = -1;

    const isSummaryRow = (row) => {
      const rowText = (row || [])
        .slice(0, 13)
        .map((cell) => String(cell || "").trim().toLowerCase())
        .join(" ");
      return summaryKeywords.some((keyword) => rowText.includes(keyword));
    };

    const isDataRowEmpty = (row) =>
      (row || []).slice(1, 13).every((cell) => String(cell || "").trim() === "");

    for (let i = 8; i < scanRange.values.length; i++) {
      if (isSummaryRow(scanRange.values[i])) {
        summaryStartIdx = i;
        break;
      }
    }

    const searchLimit = summaryStartIdx === -1 ? scanRange.values.length : summaryStartIdx;
    let emptyBlockStart = -1;
    let emptyBlockLength = 0;

    for (let i = 8; i < searchLimit; i++) {
      if (isDataRowEmpty(scanRange.values[i])) {
        if (emptyBlockStart === -1) {
          emptyBlockStart = i;
          emptyBlockLength = 1;
        } else {
          emptyBlockLength += 1;
        }

        if (emptyBlockLength >= neededRows) {
          return emptyBlockStart + 1;
        }
      } else if (emptyBlockStart !== -1) {
        break;
      }
    }

    if (summaryStartIdx !== -1) {
      const summaryRow = summaryStartIdx + 1;

      if (emptyBlockStart !== -1 && emptyBlockStart < summaryStartIdx) {
        const missingRows = neededRows - emptyBlockLength;
        if (missingRows > 0) {
          const insertRange = sheet.getRange(`${summaryRow}:${summaryRow + missingRows - 1}`);
          insertRange.insert(Excel.InsertShiftDirection.down);
          await context.sync();
        }
        return emptyBlockStart + 1;
      }

      const insertRange = sheet.getRange(`${summaryRow}:${summaryRow + neededRows - 1}`);
      insertRange.insert(Excel.InsertShiftDirection.down);
      await context.sync();
      return summaryRow;
    }

    return usedRange.rowCount + 1;
  };

  // Apply a standard variant: fetch all resources and add them to the sheet
  const handleApplyVariant = async (variant) => {
    setError("");
    setSuccessMsg("");
    if (!token) return;

    try {
      setBusy(true);
      const resources = await fetchVariantResources(token, variant.id);
      if (!resources || resources.length === 0) {
        setError("ვარიანტს არ აქვს რესურსები.");
        return;
      }

      await Excel.run(async (context) => {
        let sheet;
        if (targetSheet) {
          sheet = context.workbook.worksheets.getItemOrNullObject(getSafeSheetName(targetSheet));
          sheet.load("name");
          await context.sync();
          if (sheet.isNullObject) sheet = context.workbook.worksheets.getActiveWorksheet();
        } else {
          sheet = context.workbook.worksheets.getActiveWorksheet();
        }

        const usedRange = sheet.getUsedRangeOrNullObject();
        usedRange.load(["values", "rowCount", "columnCount"]);
        await context.sync();

        const summaryKeywords = ["ჯამი", "ზედნადები", "გეგმიური", "დაგროვება", "total", "overhead"];
        const usedValues = usedRange.isNullObject ? [] : (usedRange.values || []);

        const isSummaryRow = (row) => {
          const rowText = (row || [])
            .slice(0, 13)
            .map((cell) => String(cell || "").trim().toLowerCase())
            .join(" ");
          return summaryKeywords.some((keyword) => rowText.includes(keyword));
        };

        const isRealStandardHeader = (row) => {
          const nVal = String(row?.[0] || "").trim();
          const descVal = String(row?.[1] || "").trim();
          if (!/^\d+(?:\.0)?$/.test(nVal)) return false;
          if (!descVal || descVal.includes("თავი")) return false;
          return true;
        };

        let maxStandardNo = 0;
        for (let i = 8; i < usedValues.length; i++) {
          if (isSummaryRow(usedValues[i])) break;
          if (isRealStandardHeader(usedValues[i])) {
            const num = parseInt(String(usedValues[i][0]).split(".")[0], 10);
            if (Number.isFinite(num) && num > maxStandardNo) {
              maxStandardNo = num;
            }
          }
        }

        const standardNo = maxStandardNo + 1;
        const standardTitle = variant.full_work_name_geo || variant.full_work_name_eng || variant.variant_code || "სტანდარტი";

        // Build rows for the standard header and all resources
        const headerRow = [
          String(standardNo),
          standardTitle,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          variant.variant_code || "",
        ];

        const rows = resources.map((r) => {
          const qty = r.quantity || 0;
          return [
            "",                                           // A: N
            r.name_geo || r.name_eng || "",               // B: Description
            r.base_unit || "",                            // C: Unit
            "",                                           // D: Norm unit
            qty || "",                                    // E: Norm qty
            "",                                           // F: Material price (editable)
            "",                                           // G: Material total (formula below)
            "",                                           // H: Labour price (editable)
            "",                                           // I: Labour total (formula below)
            "",                                           // J: Equipment price (editable)
            "",                                           // K: Equipment total (formula below)
            "",                                           // L: Row total (formula below)
            r.resource_code || "",                         // M: Code
          ];
        });

        const allRows = [headerRow, ...rows, ["", "", "", "", "", "", "", "", "", "", "", "", ""]];
        const insertRow = await findInsertRow(sheet, context, allRows.length);
        const range = sheet.getRangeByIndexes(insertRow - 1, 0, allRows.length, 13);
        range.values = allRows;
        range.format.wrapText = true;
        range.format.verticalAlignment = "Center";

        const headerRange = sheet.getRangeByIndexes(insertRow - 1, 0, 1, 13);
        headerRange.format.font.bold = true;

        const formulaRows = rows.map((resource, index) => {
          const rowNumber = insertRow + index + 1;
          const resourceType = String(resource.resource_type || "").toUpperCase();
          const materialFormula = resourceType === "MATERIAL"
            ? `=IF(OR(F${rowNumber}="",E${rowNumber}=""),0,F${rowNumber}*E${rowNumber})`
            : "";
          const labourFormula = resourceType === "LABOR"
            ? `=IF(OR(H${rowNumber}="",E${rowNumber}=""),0,H${rowNumber}*E${rowNumber})`
            : "";
          const equipmentFormula = resourceType === "EQUIPMENT"
            ? `=IF(OR(J${rowNumber}="",E${rowNumber}=""),0,J${rowNumber}*E${rowNumber})`
            : "";

          return [
            "",
            materialFormula,
            "",
            labourFormula,
            "",
            equipmentFormula,
            `=SUM(G${rowNumber},I${rowNumber},K${rowNumber})`,
          ];
        });

        if (formulaRows.length > 0) {
          sheet.getRangeByIndexes(insertRow, 5, formulaRows.length, 7).formulas = formulaRows;
        }

        range.format.borders.getItem("EdgeBottom").style = "Hair";
        range.format.borders.getItem("EdgeBottom").color = "#cccccc";
        sheet.getRangeByIndexes(insertRow, 5, rows.length, 7).numberFormat =
          rows.map(() => ["#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000"]);

        range.format.autofitRows();
        await context.sync();
      });

      setSuccessMsg(`დაემატა ${resources.length} რესურსი: ${variant.full_work_name_geo || variant.variant_code}`);
    } catch (err) {
      setError("რესურსების დამატება ვერ მოხერხდა: " + (err.message || err));
    } finally {
      setBusy(false);
    }
  };

  const handleAddToSheet = async (item) => {
    setError("");
    setSuccessMsg("");

    try {

      // Normalize the item to tier 0 format
      const normItem = normalizeToTier0(item);
      console.log('normalizeToTier0:', normItem);

      await Excel.run(async (context) => {
        let sheet;
        if (targetSheet) {
          sheet = context.workbook.worksheets.getItemOrNullObject(getSafeSheetName(targetSheet));
          sheet.load("name");
          await context.sync();
          if (sheet.isNullObject) {
            sheet = context.workbook.worksheets.getActiveWorksheet();
          }
        } else {
          sheet = context.workbook.worksheets.getActiveWorksheet();
        }

        const insertRow = await findInsertRow(sheet, context, 3);

        const resType = normItem.resource_type || "";
        const unitRate = normItem.unit_rate || 0;
        const qty = normItem.qty_per_work_unit || 0;

        // Find the nearest previous 'work' row (N not ending with .1, .2, .3)
        let parentN = "1";
        let parentRowIdx = -1;
        let scanRows = 0;
        if (insertRow > 1) {
          // We'll scan up to 20 rows above, or until the top
          scanRows = Math.min(insertRow - 1, 20);
          const nRange = sheet.getRangeByIndexes(insertRow - 1 - scanRows, 0, scanRows, 1);
          nRange.load("values");
          await context.sync();
          for (let i = scanRows - 1; i >= 0; i--) {
            const nVal = String(nRange.values[i][0] || "").trim();
            if (nVal && !nVal.match(/\.([0-9]+)$/)) {
              parentN = nVal;
              parentRowIdx = i;
              break;
            }
          }
        }

        // Extract only the numeric-dot pattern from parentN (e.g., 1.0, 2.3.4)
        const match = parentN.match(/([0-9]+(\.[0-9]+)*)/);
        const cleanParentN = match ? match[1] : "1";
        console.log('parentN:', parentN, 'cleanParentN:', cleanParentN, 'parentRowIdx:', parentRowIdx);

        // Find all existing material rows under this parentN to determine next available child number
        let nextMaterialIdx = 1;
        if (parentRowIdx !== -1) {
          // Scan downward from parentRowIdx+1 until next work row or summary or end of used range
          const usedRange = sheet.getUsedRangeOrNullObject();
          usedRange.load(["rowCount"]);
          await context.sync();
          const startRow = insertRow - 1 - scanRows + parentRowIdx + 1;
          const maxRows = usedRange.isNullObject ? 0 : usedRange.rowCount;
          let endRow = startRow;
          // Find the next work row or summary
          for (let r = startRow; r < maxRows; r++) {
            const nCell = sheet.getRangeByIndexes(r, 0, 1, 1);
            nCell.load("values");
            await context.sync();
            const nVal = String(nCell.values[0][0] || "").trim();
            // Stop if we hit another work row (not a child) or summary
            if ((nVal && !nVal.match(new RegExp(`^${cleanParentN}\\.`))) && nVal !== parentN) break;
            // Optionally, add more summary/stop conditions here
            endRow = r;
          }
          const childRows = endRow - startRow + 1;
          console.log('childRows:', childRows);
          if (childRows > 0) {
            const childRange = sheet.getRangeByIndexes(startRow, 0, childRows, 1);
            childRange.load("values");
            await context.sync();
            let maxIdx = 0;
            for (let j = 0; j < childRows; j++) {
              const nVal = String(childRange.values[j][0] || "").trim();
              // Match N like 1.0.1, 1.0.2, ...
              const m = nVal.match(new RegExp(`^${cleanParentN}\\.([0-9]+)$`));
              if (m) {
                const idx = parseInt(m[1], 10);
                if (idx > maxIdx) maxIdx = idx;
              }
            }
            nextMaterialIdx = maxIdx + 1;
          }
        }
        console.log('nextMaterialIdx:', nextMaterialIdx);

        // The material row N: cleanParentN.nextMaterialIdx (e.g., 1.0.1, 1.0.2, ...)
        const materialN = cleanParentN + "." + nextMaterialIdx;
        // The labor row N: materialN + ".1" (e.g., 1.0.1.1, 1.0.2.1, ...)
        const laborN = materialN + ".1";
        console.log('materialN:', materialN, 'laborN:', laborN);

        const rowData = [
          materialN,                                              // A: N (material)
          normItem.title || "",                                   // B: Description
          normItem.base_unit || "",                               // C: Unit
          "",                                                     // D: Norm unit
          qty || "",                                              // E: Norm qty
          resType === "MATERIAL" ? unitRate : "",                // F: Material price
          resType === "MATERIAL" ? (unitRate * (qty || 1)) : "",  // G: Material total
          resType === "LABOR" ? unitRate : "",                   // H: Labour price
          resType === "LABOR" ? (unitRate * (qty || 1)) : "",     // I: Labour total
          resType === "EQUIPMENT" ? unitRate : "",               // J: Equipment price
          resType === "EQUIPMENT" ? (unitRate * (qty || 1)) : "", // K: Equipment total
          unitRate * (qty || 1),                                  // L: Row total
          normItem.work_code || "",                               // M: Code
        ];
        console.log('rowData:', rowData);

        const laborRow = [
          laborN,
          "შრომითი დანახარჯები", // B: Description
          "", "", "", "", "", "", "", "", "", "", ""
        ];
        console.log('laborRow:', laborRow);

        const emptyRow = ["", "", "", "", "", "", "", "", "", "", "", "", ""];

        // Insert all three rows at once
        const allRows = [rowData, laborRow, emptyRow];
        const range = sheet.getRangeByIndexes(insertRow - 1, 0, 3, 13);
        range.values = allRows;
        console.log('Inserted rows at:', insertRow - 1);

        // Style the new rows
        range.format.borders.getItem("EdgeBottom").style = "Hair";
        range.format.borders.getItem("EdgeBottom").color = "#cccccc";
        range.format.borders.getItem("EdgeRight").style = "Hair";
        range.format.borders.getItem("EdgeRight").color = "#cccccc";
        range.format.verticalAlignment = "Center";
        sheet.getRangeByIndexes(insertRow - 1, 5, 3, 7).numberFormat = [["#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000"], ["#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000"], ["#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000"]];

        await context.sync();
      });

      setSuccessMsg(`Added: ${item.title} + შრომითი დანახარჯები`);
    } catch (err) {
      console.error('Add to sheet error:', err);
      setError("Failed to add: " + (err.message || err));
    }
  };

  const typeLabel = (rt) => {
    if (rt === "LABOR") return "ხელფასი";
    if (rt === "EQUIPMENT") return "მექანიზმი";
    if (rt === "MATERIAL") return "მასალა";
    return rt || "—";
  };

  const typeBadgeColor = (rt) => {
    if (rt === "LABOR") return "#2196F3";
    if (rt === "EQUIPMENT") return "#FF9800";
    if (rt === "MATERIAL") return "#4CAF50";
    return "#999";
  };

  return (
    <div className="sheet-lookup-panel" data-no-translate="true">
      {/* Format selector (Tier 0 / Tier 1) */}
      <div className="sheet-lookup-chip-row">
        {FORMAT_TYPES.map((ft) => (
          <button
            key={ft.value}
            onClick={() => handleFormatChange(ft.value)}
            className={`sheet-lookup-chip ${tableFormat === ft.value ? "active" : ""}`}
          >
            {ft.label}
          </button>
        ))}
      </div>

      {/* Language selector (visible for tier1 or all) */}
      {tableFormat !== "tier0" && (
        <div className="sheet-lookup-chip-row">
          {LANGUAGES.map((l) => (
            <button
              key={l.value}
              onClick={() => handleLangChange(l.value)}
              className={`sheet-lookup-chip sheet-lookup-chip--small ${lang === l.value ? "active" : ""}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}

      {/* ═══ Standards browsing mode ═══ */}
      {tableFormat === "standards" && (
        <div className="sheet-lookup-block">
          {/* Catalog selector */}
          <select
            value={selectedCatalogId}
            onChange={(e) => { setSelectedCatalogId(e.target.value); setSelectedGroupId(""); setStdVariants([]); }}
            className="sheet-lookup-select"
          >
            <option value="">სტანდარტი...</option>
            {catalogs.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name_geo || c.name_eng}</option>
            ))}
          </select>

          {/* Groups list */}
          {selectedCatalogId && stdGroups.length > 0 && (
            <div className="sheet-lookup-list">
              {stdGroups.map((g) => (
                <div
                  key={g.id}
                  onClick={() => { setSelectedGroupId(g.id); setStdVariants([]); }}
                  className={`sheet-lookup-list-item ${selectedGroupId === g.id ? "active" : ""}`}
                >
                  <strong>{g.group_code}</strong> — {g.name_geo}
                </div>
              ))}
            </div>
          )}

          {/* Variants list with "Apply" buttons */}
          {stdVariants.length > 0 && (
            <div className="sheet-lookup-list sheet-lookup-list--tall">
              {stdVariants.map((v) => (
                <div
                  key={v.id}
                  className="sheet-lookup-result"
                >
                  <div className="sheet-lookup-variant-main">
                    <strong>{v.variant_code}</strong>
                    <div className="sheet-lookup-variant-name">
                      {(v.full_work_name_geo || v.name_geo || "").slice(0, 80)}
                    </div>
                    <div className="sheet-lookup-variant-unit">{v.base_unit}</div>
                  </div>
                  <button
                    onClick={() => handleApplyVariant(v)}
                    disabled={busy}
                    className="sheet-lookup-add"
                  >
                    + დამატება
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ TIER 0 / TIER 1 mode (catalog system) ═══ */}
      {(tableFormat === "tier0" || tableFormat === "tier1") && (
        <Tier1ResourceLookup
          token={token}
          targetSheet={targetSheet}
          projectArea={projectArea}
          lang={lang || "geo"}
          tier={tableFormat === "tier0" ? 0 : 1}
        />
      )}

      {/* Legacy table selector — only when no tier filter is selected */}
      {tableFormat !== "standards" && tableFormat !== "tier1" && tableFormat !== "tier0" && filteredTables.length > 0 && (
        <div className="sheet-lookup-block">
          <select
            value={selectedTableId}
            onChange={(e) => handleTableChange(e.target.value)}
            className="sheet-lookup-select"
          >
            <option value="">ყველა ცხრილი / All tables</option>
            {filteredTables.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name} {t.format === "tier1" ? "(Tier 1)" : "(Tier 0)"}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Resource type filter — only for legacy mode (tier0/tier1 have their own) */}
      {tableFormat !== "standards" && tableFormat !== "tier1" && tableFormat !== "tier0" && <div className="sheet-lookup-chip-row">
        {RESOURCE_TYPES.map((rt) => (
          <button
            key={rt.value}
            onClick={() => handleTypeChange(rt.value)}
            className={`sheet-lookup-chip ${resourceType === rt.value ? "active" : ""}`}
          >
            {rt.label}
          </button>
        ))}
      </div>}

      {/* Legacy search input — only when no tier filter is selected */}
      {tableFormat !== "standards" && tableFormat !== "tier1" && tableFormat !== "tier0" && <>
      <div className="sheet-lookup-search-wrap">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setShowDropdown(true);
            } else {
              doSearch(query, resourceType, tableFormat, selectedTableId, lang);
            }
          }}
          placeholder="Search materials, labor, equipment..."
          className="sheet-lookup-search"
        />
        {busy && (
          <span className="sheet-lookup-busy">
            ...
          </span>
        )}

        {/* Dropdown results */}
        {showDropdown && results.length > 0 && (
          <div
            ref={dropdownRef}
            className="sheet-lookup-dropdown"
          >
            {results.map((item, idx) => (
              <div
                key={idx}
                className="sheet-lookup-result"
                onClick={() => {
                  handleAddToSheet(item);
                }}
              >
                {/* Type badge */}
                <span
                  className="sheet-lookup-badge"
                  style={{ background: typeBadgeColor(item.resource_type) }}
                >
                  {typeLabel(item.resource_type)}
                </span>

                {/* Name */}
                <span className="sheet-lookup-title">
                  {item.title}
                </span>

                {/* Unit */}
                <span className="sheet-lookup-unit">
                  {item.base_unit}
                </span>

                {/* Price */}
                <span className="sheet-lookup-price">
                  {item.unit_rate || 0}
                </span>

                {/* Add button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToSheet(item);
                  }}
                  className="sheet-lookup-add"
                >
                  + Add
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </>}

      {error && <div className="sheet-lookup-error">{error}</div>}
      {successMsg && <div className="sheet-lookup-success">{successMsg}</div>}

      {tableFormat !== "standards" && tableFormat !== "tier1" && tableFormat !== "tier0" && !showDropdown && !busy && results.length === 0 && (
        <div className="sheet-lookup-hint">
          ძიებისთვის აირჩიეთ ცხრილი ან ჩაწერეთ საძიებო სიტყვა.
        </div>
      )}
    </div>
  );
}
